import { createContext } from 'react';

/**
 * The auth context object, kept separate from the provider component.
 *
 * React Fast Refresh only preserves state for modules that export components
 * exclusively. Splitting the context and hooks out of `auth.tsx` keeps editing
 * the provider from remounting the whole app during development.
 */

export interface Identity {
  user_id: string;
  tenant_id: string;
  roles: string[];
  amr: string[];
  /**
   * Product modules switched on for this workspace, e.g. `collaboration`.
   * Used to decide which navigation to show; the API enforces the same flags
   * independently, so hiding is a courtesy, not the control.
   */
  features?: Record<string, boolean>;
}

/** What a password login resolved to. */
export interface LoginOutcome {
  /** A second factor is enrolled and a code is needed. */
  mfaRequired: boolean;
  /** The role mandates MFA and no factor exists yet — enrol before signing in. */
  enrolmentRequired: boolean;
}

/** What the server issues once, at the start of enrolment. */
export interface EnrolmentSecrets {
  /** otpauth:// URL, rendered as a QR code. Contains the shared secret. */
  uri: string;
  secret: string;
  recovery_codes: string[];
}

export interface AuthState {
  identity: Identity | null;
  /** True until the initial refresh attempt settles, so guards do not flash. */
  initialising: boolean;
  /** Set when a password login needs a second factor or an enrolment. */
  pendingChallenge: string | null;
  workspace: string | null;

  login: (workspace: string, email: string, password: string) => Promise<LoginOutcome>;
  verifyMfa: (code: string) => Promise<void>;
  /** Generates a secret. Returns values shown to the user exactly once. */
  beginEnrolment: (replace?: boolean) => Promise<EnrolmentSecrets>;
  /** Proves the authenticator works, stores the recovery hashes, signs in. */
  confirmEnrolment: (code: string, recoveryCodes: string[]) => Promise<void>;
  /** Completes a login with a single-use recovery code. */
  recoverWithCode: (code: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthState | null>(null);
